/** Single-day schedule grid: hourly slots with jobs/inquiries placed in slots; empty slots are bookable. */

const SLOT_MINUTES = 60;

export const DAY_START_HOUR = 7;
export const DAY_END_HOUR = 18;

export function toDateKey(isoOrDate) {
  if (!isoOrDate) return null;
  const s = String(isoOrDate);
  if (s.length >= 10 && /^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
  const d = new Date(isoOrDate);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString().slice(0, 10);
}

export function parseTimeToMinutes(timeStr) {
  if (!timeStr) return null;
  const t = String(timeStr).trim();
  const m = t.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?/);
  if (!m) return null;
  const h = parseInt(m[1], 10);
  const min = parseInt(m[2], 10);
  if (Number.isNaN(h) || Number.isNaN(min)) return null;
  return h * 60 + min;
}

export function minutesToTimeInput(mins) {
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

/** Build [startMinutes, endMinutes) for each hour slot in the working day. */
export function buildDaySlots() {
  const slots = [];
  for (let hour = DAY_START_HOUR; hour < DAY_END_HOUR; hour++) {
    const start = hour * 60;
    const end = start + SLOT_MINUTES;
    slots.push({ hour, startMinutes: start, endMinutes: end, labelStart: start, labelEnd: end });
  }
  return slots;
}

function itemDateKey(kind, payload) {
  if (kind === 'job') {
    return toDateKey(payload.scheduled_date);
  }
  return toDateKey(payload.preferred_date || payload.created_at);
}

function itemMinutes(kind, payload) {
  if (kind === 'job') {
    return parseTimeToMinutes(payload.scheduled_time);
  }
  return parseTimeToMinutes(payload.preferred_time);
}

/**
 * Returns { bySlot: Map<slotIndex, entries[]>, noTime: entries[] }
 * entries: { kind, job?|inquiry? }
 */
export function assignItemsToDaySlots(dateKey, jobs, inquiries) {
  const slots = buildDaySlots();
  const bySlot = new Map();
  slots.forEach((_, i) => bySlot.set(i, []));

  const noTime = [];

  const push = (kind, data) => {
    const entry = kind === 'job' ? { kind, job: data } : { kind, inquiry: data };
    const dk = itemDateKey(kind, data);
    if (dk !== dateKey) return;

    const mins = itemMinutes(kind, data);
    if (mins === null || mins === undefined) {
      noTime.push(entry);
      return;
    }

    let placed = false;
    for (let i = 0; i < slots.length; i++) {
      const { startMinutes, endMinutes } = slots[i];
      if (mins >= startMinutes && mins < endMinutes) {
        bySlot.get(i).push(entry);
        placed = true;
        break;
      }
    }
    if (!placed) {
      noTime.push(entry);
    }
  };

  (jobs || []).forEach((j) => push('job', j));
  (inquiries || []).forEach((q) => push('inquiry', q));

  return { slots, bySlot, noTime };
}

export function formatSlotRangeLabel(startMinutes, endMinutes) {
  const fmt = (m) => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    d.setMinutes(m);
    return d.toLocaleTimeString('en-AU', { hour: 'numeric', minute: '2-digit', hour12: true });
  };
  return `${fmt(startMinutes)} – ${fmt(endMinutes)}`;
}
